import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Truck, Search, Package, Clock, MapPin, CheckCircle2 } from "lucide-react";

const BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const label = (s) => (s || "").replace(/_/g, " ");

export default function TrackOrder() {
  const { awb: awbRaw, token: tokenParam } = useParams();
  const awbParam = awbRaw || tokenParam;
  const [awb, setAwb] = useState(awbParam || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = useCallback(async (code) => {
    if (!code) return;
    setLoading(true); setError(""); setData(null);
    try {
      const r = await fetch(`${BASE}/marketplace/track?awb=${encodeURIComponent(code)}`);
      const j = await r.json();
      if (!r.ok) { setError(j.detail || "Could not find that tracking number."); }
      else setData(j);
    } catch { setError("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (awbParam) track(awbParam); }, [awbParam, track]);

  const delivered = data?.status === "delivered";

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="track-order-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-2">
          <Truck className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-900">Track your order</h1>
        </div>

        <div className="flex gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex flex-1 items-center gap-2 px-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input value={awb} onChange={(e) => setAwb(e.target.value)} onKeyDown={(e) => e.key === "Enter" && track(awb.trim())}
              placeholder="Enter your AWB / tracking number" data-testid="track-input" className="w-full py-2 text-sm outline-none" />
          </div>
          <button onClick={() => track(awb.trim())} disabled={loading} data-testid="track-btn"
            className="rounded-xl brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{loading ? "Tracking…" : "Track"}</button>
        </div>

        {error && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="track-error">{error}</div>}

        {data && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="track-result">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400">Order</p>
                <p className="text-lg font-bold text-slate-900">{data.order_number}</p>
              </div>
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold capitalize ${delivered ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`} data-testid="track-status">
                {delivered && <CheckCircle2 className="h-3.5 w-3.5" />}{label(data.shipping_status || data.status)}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-600 sm:grid-cols-3">
              <p><Package className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{data.item_count} item(s)</p>
              <p>Courier: <b>{data.courier || "—"}</b></p>
              <p>AWB: <b>{data.awb}</b></p>
              {data.destination_city && <p><MapPin className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{data.destination_city}</p>}
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="mb-3 text-sm font-semibold text-slate-800">Journey</p>
              {(data.tracking_history || []).length === 0 ? (
                <p className="text-sm text-slate-400">No tracking updates yet.</p>
              ) : (
                <div className="space-y-3">
                  {[...data.tracking_history].reverse().map((h, i) => (
                    <div key={`${h.at}-${i}`} className="flex gap-3">
                      <div className="mt-1 flex flex-col items-center">
                        <div className={`h-2.5 w-2.5 rounded-full ${i === 0 ? "bg-blue-500" : "bg-slate-300"}`} />
                        {i < data.tracking_history.length - 1 && <div className="h-full w-px flex-1 bg-slate-200" />}
                      </div>
                      <div className="pb-2">
                        <p className="text-sm font-medium capitalize text-slate-800">{label(h.status)}</p>
                        {h.description && <p className="text-xs text-slate-500">{h.description}</p>}
                        <p className="text-[11px] text-slate-400"><Clock className="mr-1 inline h-3 w-3" />{h.at ? new Date(h.at).toLocaleString() : ""}{h.location ? ` · ${h.location}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
